import React from "react";
import { FlaskConical, Medal, Award, Crown, Gem, Sparkles } from "lucide-react";

/**
 * Premium per-tier visual config for the VIP Membership Card.
 * Each tier owns its own gradient background, glow colour, accent text,
 * icon and a "shine" sweep animation.
 */
export const TIER_VISUALS = {
    none: {
        label: "Researcher",
        Icon: FlaskConical,
        // Dark graphite/black laboratory theme with cyan accents
        bg: "linear-gradient(135deg, #0b0d10 0%, #14181d 50%, #0b0d10 100%)",
        pattern: "radial-gradient(circle at 20% 30%, rgba(125,232,255,0.10), transparent 40%), radial-gradient(circle at 80% 70%, rgba(125,232,255,0.06), transparent 50%)",
        accent: "#7DE8FF",
        accentSoft: "rgba(125,232,255,0.35)",
        glow: "0 0 60px rgba(125,232,255,0.25), inset 0 0 30px rgba(125,232,255,0.04)",
        border: "rgba(125,232,255,0.30)",
        nameClass: "text-white",
        discountClass: "text-[#7DE8FF]",
        emojiBadge: "🧪",
    },
    bronze: {
        label: "Bronze",
        Icon: Medal,
        // Brushed bronze metal with copper lighting
        bg: "linear-gradient(135deg, #1a0d05 0%, #4a2515 35%, #7a3c1a 60%, #2a160a 100%)",
        pattern: "radial-gradient(circle at 30% 20%, rgba(255,165,80,0.35), transparent 45%), radial-gradient(circle at 70% 80%, rgba(205,127,50,0.20), transparent 50%)",
        accent: "#E0996D",
        accentSoft: "rgba(224,153,109,0.45)",
        glow: "0 0 60px rgba(205,127,50,0.45), inset 0 0 30px rgba(205,127,50,0.10)",
        border: "rgba(224,153,109,0.45)",
        nameClass: "text-[#FFD5B0]",
        discountClass: "text-[#FFB378]",
        emojiBadge: "🥉",
    },
    silver: {
        label: "Silver",
        Icon: Award,
        // Chrome/silver metallic
        bg: "linear-gradient(135deg, #1a1c20 0%, #4a4f57 35%, #b8bcc4 55%, #4a4f57 80%, #1a1c20 100%)",
        pattern: "radial-gradient(circle at 25% 25%, rgba(255,255,255,0.30), transparent 40%), radial-gradient(circle at 75% 75%, rgba(200,200,210,0.20), transparent 50%)",
        accent: "#E8ECF4",
        accentSoft: "rgba(232,236,244,0.45)",
        glow: "0 0 60px rgba(220,224,232,0.40), inset 0 0 30px rgba(255,255,255,0.08)",
        border: "rgba(232,236,244,0.45)",
        nameClass: "text-white",
        discountClass: "text-white",
        emojiBadge: "🥈",
    },
    gold: {
        label: "Gold",
        Icon: Crown,
        // Premium black & gold with lightning vortex effects
        bg: "linear-gradient(135deg, #0a0905 0%, #1a1208 30%, #3a2a08 55%, #1a1208 80%, #0a0905 100%)",
        pattern: "radial-gradient(circle at 20% 30%, rgba(233,188,11,0.55), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,215,0,0.30), transparent 50%), radial-gradient(circle at 50% 50%, rgba(233,188,11,0.15), transparent 65%)",
        accent: "var(--gold)",
        accentSoft: "rgba(233,188,11,0.55)",
        glow: "0 0 70px rgba(233,188,11,0.55), inset 0 0 35px rgba(233,188,11,0.10)",
        border: "rgba(233,188,11,0.55)",
        nameClass: "text-[#FFE9A6]",
        discountClass: "text-[var(--gold)]",
        emojiBadge: "🥇",
    },
    platinum: {
        label: "Platinum",
        Icon: Gem,
        // Ice chrome with blue glow & crystal highlights
        bg: "linear-gradient(135deg, #0a1018 0%, #1a3050 35%, #4a85b8 55%, #1a3050 80%, #0a1018 100%)",
        pattern: "radial-gradient(circle at 30% 25%, rgba(170,220,255,0.40), transparent 45%), radial-gradient(circle at 75% 75%, rgba(120,180,230,0.30), transparent 50%), radial-gradient(circle at 50% 50%, rgba(255,255,255,0.10), transparent 65%)",
        accent: "#AADCFF",
        accentSoft: "rgba(170,220,255,0.50)",
        glow: "0 0 70px rgba(120,180,230,0.55), inset 0 0 35px rgba(170,220,255,0.10)",
        border: "rgba(170,220,255,0.55)",
        nameClass: "text-white",
        discountClass: "text-[#AADCFF]",
        emojiBadge: "💎",
    },
    diamond: {
        label: "Diamond",
        Icon: Sparkles,
        // Black carbon fibre + purple/blue crystal effects
        bg: "linear-gradient(135deg, #050208 0%, #1a0830 30%, #2a0850 50%, #08184a 75%, #050208 100%)",
        pattern: "radial-gradient(circle at 20% 20%, rgba(180,80,255,0.55), transparent 45%), radial-gradient(circle at 80% 80%, rgba(80,140,255,0.45), transparent 50%), radial-gradient(circle at 50% 30%, rgba(255,255,255,0.10), transparent 60%)",
        accent: "#C896FF",
        accentSoft: "rgba(200,150,255,0.55)",
        glow: "0 0 80px rgba(180,100,255,0.55), 0 0 100px rgba(80,140,255,0.30), inset 0 0 40px rgba(180,100,255,0.10)",
        border: "rgba(200,150,255,0.60)",
        nameClass: "text-white",
        discountClass: "text-[#D8B8FF]",
        emojiBadge: "💠",
    },
};

/**
 * Premium VIP Membership Card — hero unit on the account dashboard.
 * Renders as a 3D-feeling card with tier-specific finish, metallic shine sweep,
 * pulsing badge, and a hover lift.
 */
export const VipMembershipCard = ({
    tier = "none",
    customerName = "",
    lifetimeSpend = 0,
    discountPct = 0,
    naturalTier = null,
    overridden = false,
    cardRef = null,
    className = "",
}) => {
    const v = TIER_VISUALS[tier] || TIER_VISUALS.none;
    const Icon = v.Icon;

    return (
        <div
            ref={cardRef}
            className={`vip-card group relative rounded-3xl overflow-hidden ${className}`}
            style={{
                aspectRatio: "1.586 / 1",
                background: v.bg,
                boxShadow: v.glow,
                border: `1px solid ${v.border}`,
            }}
            data-testid={`vip-membership-card-${tier}`}
        >
            {/* Pattern layer */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ backgroundImage: v.pattern }}
            />

            {/* Carbon-fibre weave for diamond, subtle grid for others */}
            <div
                className="absolute inset-0 opacity-[0.08] pointer-events-none"
                style={{
                    backgroundImage:
                        tier === "diamond"
                            ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.4) 0 1px, transparent 1px 6px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.4) 0 1px, transparent 1px 6px)"
                            : tier === "silver"
                                ? "repeating-linear-gradient(90deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 4px)"
                                : "repeating-linear-gradient(0deg, rgba(255,255,255,0.25) 0 1px, transparent 1px 3px)",
                }}
            />

            {/* Metallic shine sweep */}
            <div className="vip-card-shine pointer-events-none" />

            {/* Inner content */}
            <div className="relative z-10 h-full flex flex-col justify-between p-5 sm:p-7">
                {/* Top row: Carbon Labs brand + status badge */}
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.4em] text-white/70">
                            Carbon Labs
                        </p>
                        <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mt-0.5">
                            VIP Membership
                        </p>
                    </div>
                    <div
                        className="vip-badge-pulse flex items-center gap-1.5 px-2.5 py-1 rounded-full border backdrop-blur-sm"
                        style={{
                            borderColor: v.accentSoft,
                            background: `linear-gradient(135deg, ${v.accentSoft}, transparent)`,
                        }}
                        data-testid={`vip-badge-${tier}`}
                    >
                        <Icon size={12} style={{ color: v.accent }} />
                        <span className="text-[10px] font-mono uppercase tracking-widest font-bold" style={{ color: v.accent }}>
                            {v.label}
                        </span>
                    </div>
                </div>

                {/* Middle: huge tier label + icon */}
                <div className="flex items-end justify-between gap-4 my-2">
                    <div className="flex-1 min-w-0">
                        <h2
                            className={`font-display font-black uppercase tracking-tight leading-none ${v.nameClass}`}
                            style={{
                                fontSize: "clamp(1.75rem, 5.5vw, 3rem)",
                                textShadow: `0 0 30px ${v.accentSoft}`,
                                letterSpacing: "0.03em",
                            }}
                            data-testid="vip-card-tier-label"
                        >
                            {v.label}
                        </h2>
                        <p className={`font-display font-bold mt-1 ${v.discountClass}`}
                            style={{ fontSize: "clamp(1.25rem, 3.5vw, 1.75rem)" }}>
                            {discountPct}<span className="text-base sm:text-xl">% OFF</span>
                        </p>
                    </div>
                    <div
                        className="vip-icon-pulse w-14 h-14 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center shrink-0"
                        style={{
                            background: `radial-gradient(circle, ${v.accentSoft}, transparent 70%)`,
                            border: `1px solid ${v.accentSoft}`,
                        }}
                    >
                        <Icon size={32} className="sm:hidden" style={{ color: v.accent }} />
                        <Icon size={44} className="hidden sm:block" style={{ color: v.accent }} />
                    </div>
                </div>

                {/* Bottom row: holder name + lifetime spend */}
                <div className="flex items-end justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/40">
                            Member
                        </p>
                        <p className="text-sm sm:text-base font-display font-bold text-white truncate max-w-[200px] sm:max-w-[260px]" data-testid="vip-card-name">
                            {customerName || "Carbon Researcher"}
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-[9px] font-mono uppercase tracking-[0.3em] text-white/40">
                            Lifetime Spend
                        </p>
                        <p className="text-sm sm:text-base font-mono font-bold tabular-nums" style={{ color: v.accent }} data-testid="vip-card-spend">
                            A${Number(lifetimeSpend || 0).toFixed(2)}
                        </p>
                    </div>
                </div>
            </div>

            {overridden && (
                <div className="absolute top-3 right-3 z-20 px-2 py-0.5 rounded-full bg-black/60 border border-white/20 text-[8px] font-mono uppercase tracking-widest text-white/60">
                    Manual · natural: {naturalTier || "none"}
                </div>
            )}
        </div>
    );
};
